import isObject from '../utility/isObject';

export type OrderConditions = {
  target: string;
  direction?: 'asc' | 'desc';
};

export type FirstPageConditions = {
  first: number | null;
  order?: OrderConditions | null;
};

export type NextPageConditions = {
  first: number | null;
  after: string | null;
  order?: OrderConditions | null;
};

export type PreviousPageConditions = {
  last: number | null;
  before: string | null;
  order?: OrderConditions | null;
};

export type PageConditions = NextPageConditions | PreviousPageConditions | FirstPageConditions;

class PageInfo {
  public startCursor: string | null;
  public endCursor: string | null;
  public hasNextPage: boolean;
  public hasPreviousPage: boolean;
  public displayNum: number;
  public loadNum: number;

  constructor ({
    startCursor = null,
    endCursor = null,
    hasNextPage = false,
    hasPreviousPage = false,
    displayNum = 30,
    loadNum = 30,
  }: {
    startCursor?: string | null;
    endCursor?: string | null;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    displayNum?: number;
    loadNum?: number;
  }) {
    this.startCursor = startCursor;
    this.endCursor = endCursor;
    this.hasNextPage = hasNextPage;
    this.hasPreviousPage = hasPreviousPage;
    this.displayNum = displayNum;
    this.loadNum = loadNum;
  }

  public static fromJson (value: any): PageInfo {
    if (!isObject(value)) {
      throw new TypeError('value must be an object for PageInfo.fromJson(value).');
    }

    const startCursor = value['startCursor'];
    const endCursor = value['endCursor'];
    const hasNextPage = value['hasNextPage'];
    const hasPreviousPage = value['hasPreviousPage'];

    if (typeof hasNextPage !== 'boolean') {
      throw new TypeError('value.hasNextPage must be an boolean for PageInfo.fromJson(value).');
    }

    if (typeof hasPreviousPage !== 'boolean') {
      throw new TypeError('value.hasPreviousPage must be an boolean for PageInfo.fromJson(value).');
    }

    if (startCursor && typeof startCursor !== 'string') {
      throw new TypeError('value.startCursor must be a string for PageInfo.fromJson(value).');
    }

    if (endCursor && typeof endCursor !== 'string') {
      throw new TypeError('value.endCursor must be a string for PageInfo.fromJson(value).');
    }

    return new PageInfo({
      startCursor,
      endCursor,
      hasNextPage,
      hasPreviousPage,
    });
  }

  public firstPageConditions (order: OrderConditions | null = null): FirstPageConditions {
    return {
      first: this.loadNum,
      order,
    };
  }

  public nextPageConditions (order: OrderConditions | null = null): NextPageConditions {
    if (!this.hasNextPage || !this.endCursor) {
      throw new Error();
    }
    return {
      first: this.loadNum,
      after: this.endCursor,
      order,
    };
  }

  public previousPageConditions (order: OrderConditions | null = null): PreviousPageConditions {
    if (!this.hasPreviousPage || !this.startCursor) {
      throw new Error();
    }
    return {
      last: this.loadNum,
      before: this.startCursor,
      order,
    };
  }

  public previousPageInfo (
    hasPreviousPage: boolean,
    startCursor: string | null,
    displayNum?: number | null,
    loadNum?: number | null,
  ): PageInfo {
    return new PageInfo({
      hasPreviousPage,
      startCursor: startCursor || this.startCursor,
      displayNum: displayNum || this.displayNum,
      loadNum: loadNum || this.loadNum,
      hasNextPage: this.hasNextPage,
      endCursor: this.endCursor,
    });
  }

  public nextPageInfo (
    hasNextPage: boolean,
    endCursor: string | null,
    displayNum?: number | null,
    loadNum?: number | null,
  ): PageInfo {
    return new PageInfo({
      hasNextPage,
      endCursor: endCursor || this.endCursor,
      displayNum: displayNum || this.displayNum,
      loadNum: loadNum || this.loadNum,
      hasPreviousPage: this.hasPreviousPage,
      startCursor: this.startCursor,
    });
  }
}

export default PageInfo;
